import { Task } from "model/activitiesModel";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useSSE from "./useSSE";
import { Organization } from "model/organizationModel";
import { getUserData } from "common/utilities";
import { fetchTodo, fetchDoing, setManualOrder } from "api/activitiesApi";

type Tasks = { todo: Task[]; doing: Task[] };

// 0 = nessuna paginazione: il server torna l'intera lista (Service.Processes:
// con APage/APageSize a 0 passa NULL come limit, e in PG "LIMIT NULL" = nessun
// limite). NON rimettere 50 senza aver prima spostato i filtri al server: la
// pagina filtra e ordina in LOCALE (vedi sotto) e con una pagina da 50 quel
// filtraggio vedrebbe solo le prime 50 righe.
const PAGE_SIZE = 0;

// Step 5: il client chiede la pagina al server invece di consumare le liste dal
// SSE, che ora manda solo { reload: true }.
//
// Perche' PAGE_SIZE = 0: MyActivities.tsx filtra e ordina in locale
// (common/utilities.processTasks -> filterTasks + sortTasks) e costruisce i menu
// dei filtri da `tasks` (attivita', tag, userdata key). Con la paginazione
// attiva ricerca, ordinamento e menu vedrebbero solo le righe caricate, in
// silenzio. La paginazione diventa corretta solo quando i filtri passano al
// server (la funzione SQL li accetta gia', il controller oggi manda NULL):
// allora si rimette 50, si ACCUMULANO le pagine invece di sostituirle, si
// collega useInfiniteScroll e si chiama persistOrder dal drag&drop.
// L'ordinamento manuale drag&drop e' persistito server-side via setManualOrder.

const useActivities = (
  organization: Organization | null
): [
  Tasks,
  React.Dispatch<React.SetStateAction<Tasks>>,
  React.MutableRefObject<{
    todo: number[];
    doing: number[];
  }>,
  boolean,
  (kind: "todo" | "doing", page: number) => void,
  (kind: "todo" | "doing", orderedIds: number[]) => Promise<void>
] => {
  const [tasks, setTasks] = useState<Tasks>({ todo: [], doing: [] });
  const [loading, setLoading] = useState<boolean>(false);
  const [pageTodo, setPageTodo] = useState<number>(1);
  const [pageDoing, setPageDoing] = useState<number>(1);
  const orderedArray = useRef<{ todo: number[]; doing: number[] }>({
    todo: [],
    doing: [],
  });

  // `silent` = aggiornamento in background: le liste vengono sostituite senza
  // passare da `loading`, altrimenti entrambe le colonne si svuotano e mostrano
  // lo spinner. Serve per l'SSE: dopo un cambio di stato il task ha gia' il suo
  // spinner in card, e la lista deve aggiornarsi sotto, non ricaricarsi da capo.
  const reload = useCallback(async (silent = false) => {
    if (!organization?.id) return;
    if (!silent) setLoading(true);
    try {
      const [todoRes, doingRes] = await Promise.all([
        fetchTodo(organization.id, pageTodo, PAGE_SIZE),
        fetchDoing(organization.id, pageDoing, PAGE_SIZE),
      ]);
      setTasks({
        todo: todoRes.waitingflownodes as unknown as Task[],
        doing: doingRes.doingflownodes as unknown as Task[],
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [organization?.id, pageTodo, pageDoing]);

  // SSE: il server emette { reload: true } quando succede qualcosa. Noi
  // rifacciamo il fetch della pagina corrente, senza toccare `loading`.
  const handleSSEMessage = useCallback(
    (data: unknown) => {
      if (data && typeof data === "object" && (data as { reload?: boolean }).reload) {
        reload(true);
      }
    },
    [reload]
  );

  const { resetLastEventID: resetEventTasks, mounted } = useSSE<unknown>(
    organization?.id ? `tasks/canget/sse/${organization.id}` : "",
    handleSSEMessage,
    true
  );

  useEffect(() => {
    if (organization?.id && mounted) {
      resetEventTasks();
      reload();
    }
    // resetEventTasks e reload sono stabili quando organization.id cambia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, mounted]);

  // Cambio pagina: ricarica solo quella colonna.
  const setPage = useCallback(
    (kind: "todo" | "doing", page: number) => {
      if (kind === "todo") setPageTodo(page);
      else setPageDoing(page);
      // Forza un reload della colonna (la setState e' asincrona ma reload
      // legge direttamente da pageTodo/pageDoing che pero' non sono ancora
      // aggiornati: usiamo un microtask).
      setTimeout(reload, 0);
    },
    [reload]
  );

  // Drag&drop: persiste sul server la nuova posizione.
  const persistOrder = useCallback(
    async (kind: "todo" | "doing", orderedIds: number[]) => {
      if (!organization?.id) return;
      orderedArray.current[kind] = orderedIds;
      try {
        await setManualOrder({
          kind,
          idorganization: organization.id,
          orderedIds,
        });
      } catch (e) {
        // Non bloccare l'UI: il prossimo reload mostrera' l'ordine vero del server.
        // eslint-disable-next-line no-console
        console.error("setManualOrder failed", e);
      }
    },
    [organization?.id]
  );

  // Mantieni orderedArray.allineato ai tasks quando cambia la pagina.
  useEffect(() => {
    orderedArray.current.todo = tasks.todo.map((t) => t.idprocesstoken);
  }, [tasks.todo]);
  useEffect(() => {
    orderedArray.current.doing = tasks.doing.map((t) => t.idprocesstoken);
  }, [tasks.doing]);

  // Esponi un setter "all-purpose" per chi vuole ancora toccare le liste
  // direttamente (MyActivities.tsx lo usava per il riordino locale).
  const setTasksAll = useCallback<React.Dispatch<React.SetStateAction<Tasks>>>(
    (next) => setTasks(next),
    []
  );

  // ponytail: shape a 6 elementi e' backward-compatible col vecchio a 4
  // (TypeScript consente di ignorare gli ultimi). Per consumatori che vogliono
  // pagination + persistOrder: destructure di 6.
  return [tasks, setTasksAll, orderedArray, loading, setPage, persistOrder];
};

export default useActivities;
